import { useState } from 'react';

/**
 * Toggle controls object
 *
 * @typedef {Object} IToggleControls
 * @property {(val: boolean) => void} set - Set the toggle to a specific value
 * @property {() => void} setOff - Set the toggle to false
 * @property {() => void} setOn - Set the toggle to true
 * @property {() => void} toggle - Toggle the current value
 */

/**
 * Hook for managing boolean toggle state
 *
 * @param {boolean} [initialValue=false] - Initial toggle value
 * @returns {[boolean, IToggleControls]} Tuple of [current value, controls]
 *
 * @example
 * const [isOpen, { toggle, setOn, setOff }] = useToggle(false);
 *
 * // Toggle the value
 * toggle();
 *
 * // Set to specific value
 * setOn(); // true
 * setOff(); // false
 */
export const useToggle = (initialValue = false) => {
  const [value, setValue] = useState(initialValue);

  return [
    value,
    {
      set: (val) => setValue(val),
      setOff: () => setValue(false),
      setOn: () => setValue(true),
      toggle: () => setValue((v) => !v),
    },
  ];
};
